/**
 * Accessible TMenu Enhancer (Keyboard Navigation + ARIA + Focus Fix)
 * Final Stable Version
 *
 * Please note : As the menu itself is too complex, this is just a wrapper code
 * that gives it keyboard functionality. The actual fully accessible implementation
 * would require a complete rewrite of the current TMenu structure.
 *
 * Author: Khushwant
 */


(function () {
  'use strict';

  const MAX_RETRIES = 30;
  const INITIAL_DELAY = 400;
  const ACTIVE_CLASS = 'hover';
  const MENU_SELECTOR = '.tmenu_nav';
  const ROOT_SELECTOR = '.tmenu_item.tmenu_item--root';

  function log(...args) {
    console.log('[TMenu A11y]', ...args);
  }

  function warn(...args) {
    console.warn('[TMenu A11y]', ...args);
  }

  function error(...args) {
    console.error('[TMenu A11y]', ...args);
  }

  function simulateMouseHover(li, triggerElement) {
    const mouseEvents = ['mouseenter', 'mouseover'];
    mouseEvents.forEach(type => {
      li.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
      triggerElement.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
      log(`🖱️ Dispatched ${type} on <li> and trigger`);
    });
  }

  function simulateMouseLeave(li, triggerElement) {
    const mouseEvents = ['mouseleave', 'mouseout'];
    mouseEvents.forEach(type => {
      li.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
      triggerElement.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
      log(`👋 Dispatched ${type} on <li> and trigger`);
    });
  }

  function openSubmenu(item, triggerElement = null) {
    const link = item.querySelector(':scope > a.tmenu_item_link');
    if (!link) return;

    item.classList.add(ACTIVE_CLASS);
    item.setAttribute('data-open', 'true');
    link.setAttribute('aria-expanded', 'true');

    const trigger = triggerElement || link;
    simulateMouseHover(item, trigger);

    log('✅ Opened submenu for:', link.textContent.trim());
  }

  function closeSubmenu(item) {
    const link = item.querySelector(':scope > a.tmenu_item_link');
    const indicator = item.querySelector(':scope > a .tmenu_indicator') || link;
    if (!link || !indicator) return;

    item.classList.remove(ACTIVE_CLASS);
    item.setAttribute('data-open', 'false');
    link.setAttribute('aria-expanded', 'false');

    simulateMouseLeave(item, indicator);
    log('❎ Closed submenu for:', link.textContent.trim());
  }

  function enhanceItem(item, index) {
    if (item.dataset.a11yEnhanced === 'true') {
      log(`Item ${index + 1} already enhanced, skipping.`);
      return;
    }

    const link = item.querySelector(':scope > a.tmenu_item_link');
    const indicator = item.querySelector(':scope > a .tmenu_indicator');

    if (!link) {
      warn(`Item ${index + 1} has no <a>. Skipping.`);
      return;
    }

    const hasHref = link.hasAttribute('href') && link.getAttribute('href') !== 'javascript:;';
    const triggerElement = hasHref ? indicator : link;

    if (!triggerElement) {
      warn(`Item ${index + 1} has no valid trigger. Skipping.`);
      return;
    }

    // Mark as enhanced and set state
    item.dataset.a11yEnhanced = 'true';
    item.setAttribute('data-open', 'false');

    // Set accessibility attributes
    triggerElement.setAttribute('tabindex', '0');
    triggerElement.setAttribute('role', 'button');
    triggerElement.setAttribute('aria-label', 'New arrivals submenu');
    link.setAttribute('aria-haspopup', 'true');

    // Toggle submenu on Enter/Space
    triggerElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const isOpen = item.getAttribute('data-open') === 'true';
        if (isOpen) {
          log(`⬅️ Closing submenu for "${link.textContent.trim()}"`);
          closeSubmenu(item);
        } else {
          log(`🡆 Opening submenu for "${link.textContent.trim()}"`);
          openSubmenu(item, triggerElement);
        }
      }
    });

    // Close submenu on focus leave
    item.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!item.contains(document.activeElement)) {
          log(`🔄 Focus left "${link.textContent.trim()}" → closing submenu`);
          closeSubmenu(item);
        }
      }, 10);
    });

    log(`🎯 Enhanced item ${index + 1}: "${link.textContent.trim()}" using trigger:`, hasHref ? 'indicator' : 'anchor');
  }

  function enhanceMenu() {
    const menu = document.querySelector(MENU_SELECTOR);
    if (!menu) {
      warn('🚫 Menu container not found.');
      return false;
    }

    const rootItems = menu.querySelectorAll(ROOT_SELECTOR);
    if (!rootItems.length) {
      warn('🚫 No root items found in menu.');
      return false;
    }

    log(`🚀 Enhancing ${rootItems.length} menu items...`);
    rootItems.forEach(enhanceItem);
    return true;
  }

  function waitForMenu(attempt = 1, delay = INITIAL_DELAY) {
    const menuReady = document.querySelector(MENU_SELECTOR);
    const rootReady = document.querySelectorAll(ROOT_SELECTOR).length > 0;

    if (menuReady && rootReady) {
      log(`✅ Menu ready on attempt ${attempt}. Enhancing now...`);
      enhanceMenu();
      return;
    }

    if (attempt >= MAX_RETRIES) {
      error(`❌ Enhancement failed after ${MAX_RETRIES} attempts.`);
      return;
    }

    log(`⏳ Attempt ${attempt}: Menu not ready. Retrying in ${delay}ms...`);
    setTimeout(() => waitForMenu(attempt + 1, delay * 1.25), delay);
  }

  log('🔧 Starting TMenu accessibility enhancement...');
  waitForMenu();
})();

(function () {
  function initAccessibleMenuIndicators() {
    const indicators = document.querySelectorAll('.tmenu_indicator');

    indicators.forEach((indicator) => {
      if (indicator.dataset.a11yInit === 'true') return;
      indicator.dataset.a11yInit = 'true';

      indicator.setAttribute('role', 'button');
      indicator.setAttribute('tabindex', '0');

      if (!indicator.hasAttribute('aria-expanded')) {
        indicator.setAttribute('aria-expanded', 'false');
      }

      const menuItem = indicator.closest('.tmenu_item');
      const labelText = menuItem
        ?.querySelector('.tmenu_item_text')
        ?.innerText
        ?.trim();

      if (labelText) {
        indicator.setAttribute('aria-label', `${labelText} submenu`);
      } else {
        indicator.setAttribute('aria-label', 'Submenu');
      }

      indicator.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          indicator.click();
        }
      });

      indicator.addEventListener('click', () => {
        const expanded = indicator.getAttribute('aria-expanded') === 'true';
        indicator.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }

  initAccessibleMenuIndicators();

  const observer = new MutationObserver(() => {
    initAccessibleMenuIndicators();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();

function removeComments(element) {
  const iterator = document.createNodeIterator(
    element,
    NodeFilter.SHOW_COMMENT,
    null,
    false
  );

  let currentNode;
  while ((currentNode = iterator.nextNode())) {
    currentNode.remove();
  }
}

const headings = document.querySelectorAll('.card-collection__heading');

headings.forEach(heading => {
  removeComments(heading);
});

window.addEventListener('DOMContentLoaded', function () {
  const ORIGINAL_LABEL = 'Instagram post opens in a popup';

  setInterval(() => {
    const items = document.querySelectorAll('.instafeed-new-layout-item-container');

    items.forEach((item, index) => {
      const link = item;

      const wrapper = item.closest('.instafeed-new-layout-item');
      if (!wrapper) {
        return;
      }

      const img = wrapper.querySelector('img[alt]');
      if (!img) {
        return;
      }

      const currentLabel = link.getAttribute('aria-label');

      if (currentLabel && currentLabel !== ORIGINAL_LABEL) {
        return;
      }

      const altText = img.getAttribute('alt')?.trim();

      if (altText) {
        link.setAttribute('aria-label', altText);
      } else {
        const fallback = `Open Instagram post ${index + 1} in a dialog`;
        link.setAttribute('aria-label', fallback);
      }
    });
  }, 1000);

  document.addEventListener('focusin', (event) => {
  const el = event.target;

  console.log(
    '[Focus]',
    el,
    'tag:',
    el.tagName,
    'classes:',
    el.className,
    'aria-label:',
    el.getAttribute('aria-label')
  );
});

setInterval(() => {
  document.querySelectorAll('.yotpo-custom-step-icon').forEach(img => {
    if (img.hasAttribute('alt')) {
      img.removeAttribute('alt');
      console.log('[Yotpo A11y] alt removed', img);
    }
  });
}, 1000);
setInterval(() => {
  document.querySelectorAll('.yotpo-vip-tier-icon').forEach(img => {
    if (img.hasAttribute('alt')) {
      img.removeAttribute('alt');
      console.log('[Yotpo A11y] alt removed', img);
    }
  });
}, 1000);

setInterval(() => {
  document.querySelectorAll('.yotpo-outer-tile[role="listitem"]').forEach(tile => {
    if (tile.getAttribute('role') !== 'button') {
      tile.setAttribute('role', 'button');

      tile.setAttribute('tabindex', '0');

      if (!tile.hasAttribute('aria-label') || tile.getAttribute('aria-label').trim() === '') {
        const campaignName = tile.getAttribute('aria-label') || 'Earn Points Campaign';
        tile.setAttribute('aria-label', campaignName);
      }

      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          tile.click();
        }
      });

      console.log('[Yotpo Accessibility] Tile converted to button:', tile);
    }
  });
}, 1000);

const interval = setInterval(() => {
  const table = document.querySelector('.collapsible__content table');
  if (!table) return;

  clearInterval(interval);

  const colgroup = table.querySelector("colgroup");
  if (colgroup) colgroup.remove();

  const rows = table.rows;
  if (rows.length === 0) return;

  const firstRow = rows[0];
  for (let i = 0; i < firstRow.cells.length; i++) {
    const cell = firstRow.cells[i];
    const th = document.createElement("th");
    th.innerHTML = cell.innerHTML;
    th.className = cell.className;
    th.scope = "col";
    firstRow.replaceChild(th, cell);
  }

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const cell = row.cells[0];
    const th = document.createElement("th");
    th.innerHTML = cell.innerHTML;
    th.scope = "row";
    th.style.width = "auto";
    th.style.height = "auto";
    th.className = "";
    row.replaceChild(th, cell);
  }


}, 100);


});

document.addEventListener('DOMContentLoaded', () => {
  const slides = document.querySelectorAll('.slideshow__slide');
  const totalSlides = slides.length;

  slides.forEach((slide, index) => {
    const img = slide.querySelector('img');
    if (!img) return;

    const altText = img.getAttribute('alt')?.trim();
    if (!altText) return;

    const slideNumber = index + 1;

    slide.setAttribute(
      'aria-label',
      `Slide ${slideNumber} of ${totalSlides}: ${altText}`
    );
  });
});

document.addEventListener('DOMContentLoaded', () => {

  function updateSelectedSwatchAria() {
    document.querySelectorAll('.color-swatch').forEach(swatch => {
      swatch.removeAttribute('aria-current');
    });

    const selectedItem = document.querySelector('.color-swatch__item.selected');
    if (!selectedItem) return;

    const selectedSwatch = selectedItem.closest('.color-swatch');
    if (selectedSwatch) {
      selectedSwatch.setAttribute('aria-current', 'page');
    }
  }

  updateSelectedSwatchAria();

  const swatchContainer = document.querySelector('.color-swatch-list');
  if (!swatchContainer) return;

  const observer = new MutationObserver(() => {
    updateSelectedSwatchAria();
  });

  observer.observe(swatchContainer, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class']
  });

});

document.addEventListener('DOMContentLoaded', () => {
  const interval = setInterval(() => {
    const buttons = document.querySelectorAll('.yotpo-star-distribution button');
    if (buttons.length > 0) {
      buttons.forEach(button => {
        button.setAttribute('role', 'link');

        if (!button.querySelector('.visually-hidden')) {
          const hiddenText = document.createElement('span');
          hiddenText.className = 'visually-hidden';

          const match = button.getAttribute('aria-label')?.match(/(\d+) star review by (\d+) users/);
          if (match) {
            const stars = match[1];
            const users = match[2];
            hiddenText.textContent = `Selecting this will show the ${users} reviews for ${stars} star rating`;
            button.appendChild(hiddenText);
          }
        }
      });

      clearInterval(interval);
    }
  }, 500);

  if (!document.querySelector('.visually-hidden-style')) {
    const style = document.createElement('style');
    style.textContent = `
      .visually-hidden {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0 0 0 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }
    `;
    style.className = 'visually-hidden-style';
    document.head.appendChild(style);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const interval = setInterval(() => {
    const dialog = document.querySelector('.cart-drawer .dialog');

    if (!dialog || !dialog.hasAttribute('open')) return;

    const cartTitle = dialog.querySelector('#cart-drawer-heading');
    if (cartTitle && !cartTitle.dataset.focused) {
      cartTitle.setAttribute('tabindex', '-1');
      cartTitle.focus();
      cartTitle.dataset.focused = 'true';
    }

    const thumbnails = dialog.querySelectorAll('.cart-item__product-image a');
    thumbnails.forEach((thumb, index) => {
      if (!thumb.dataset.a11yEnhanced) {
        const productName = thumb.querySelector('.visually-hidden')?.textContent || `Product ${index+1}`;
        thumb.setAttribute('aria-label', `Load image ${index + 1} in gallery view for ${productName}`);
        thumb.dataset.a11yEnhanced = 'true';
      }
    });

    let liveRegion = dialog.querySelector('[data-live-region]');
    if (!liveRegion) {
      liveRegion = document.createElement('span');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('data-live-region', '');
      liveRegion.className = 'visually-hidden';
      dialog.appendChild(liveRegion);
    }

    const mainImages = dialog.querySelectorAll('.cart-item__product-image img');
    mainImages.forEach((img, idx) => {
      if (!img.dataset.liveObserved) {
        let lastSrc = img.src;
        setInterval(() => {
          if (img.src !== lastSrc) {
            lastSrc = img.src;
            liveRegion.textContent = `Image ${idx + 1} displayed`;
          }
        }, 200);
        img.dataset.liveObserved = 'true';
      }
    });

    clearInterval(interval);
  }, 300);

});

document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    document.querySelector("img.shopify-dns-link-icon").remove();
    document.querySelectorAll('.tmenu_item_link[aria-haspopup]')
    .forEach(link => {
      link.removeAttribute('aria-expanded');
      link.removeAttribute('aria-haspopup');
      link.removeAttribute('role');
    });

  }, 1000);
});

